import React, { useEffect, useState } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl } from "../../baseurl";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

function RevenueReport() {
  const [monthlyData, setMonthlyData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState("");

  // 🟩 Fetch Monthly Revenue Report
  const fetchMonthlyRevenue = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}monthlyRevenueReport`);
      if (res.data.success) {
        setMonthlyData(res.data.data || []);
        setFilteredData(res.data.data || []);
      } else {
        Swal.fire("Error", "No monthly revenue data found", "error");
      }
    } catch (err) {
      console.error("Error fetching monthly revenue:", err);
      Swal.fire("Error", "Failed to load monthly revenue data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonthlyRevenue();
  }, []);

  // 🧮 Handle Month Filter
  const handleMonthChange = (e) => {
    const monthValue = e.target.value;
    setSelectedMonth(monthValue);

    if (!monthValue) {
      setFilteredData(monthlyData);
      return;
    }

    const filtered = monthlyData.filter((row) => row.month === monthValue);
    setFilteredData(filtered);
  };

  // 📤 Export to CSV
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      Swal.fire("No data", "No records available to export", "info");
      return;
    }

    const csvHeader = ["Month", "Total Cash (₹)", "Total UPI (₹)", "Grand Total (₹)"];
    const csvRows = filteredData.map((row) => [
      row.month_label,
      row.total_cash,
      row.total_upi,
      row.total_cash + row.total_upi,
    ]);

    const csvContent = [
      csvHeader.join(","),
      ...csvRows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `RevenueReport_${selectedMonth || "All"}.csv`;
    link.click();
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper bg-light">
        <div className="container py-3">
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="fw-bold text-dark mb-0">
                  📅 Month-wise Cash & UPI Revenue
                </h4>

                <div className="d-flex gap-2">
                  {/* 🟦 Native HTML Month Picker */}
                  <input
                    type="month"
                    className="form-control"
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    style={{ width: 180 }}
                  />

                  {/* 📤 Export CSV */}
                  <button
                    className="btn btn-success"
                    onClick={exportToCSV}
                    disabled={loading || filteredData.length === 0}
                  >
                    📥 Export CSV
                  </button>
                </div>
              </div>

              {/* 🧱 Skeleton Loading */}
              {loading ? (
                <div className="py-3">
                  <Skeleton height={40} width={250} className="mb-3" />
                  <Skeleton count={8} height={30} className="mb-2" />
                </div>
              ) : filteredData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered text-center align-middle">
                    <thead className="table-header">
                      <tr>
                        <th>Month</th>
                        <th>Total Cash (₹)</th>
                        <th>Total UPI (₹)</th>
                        <th>Grand Total (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((row, i) => (
                        <tr key={i}>
                          <td className="fw-semibold">{row.month_label}</td>
                          <td className="text-success">
                            ₹
                            {row.total_cash.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                          <td className="text-primary">
                            ₹
                            {row.total_upi.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                          <td className="fw-bold text-dark">
                            ₹
                            {(row.total_cash + row.total_upi).toLocaleString(
                              "en-IN",
                              { minimumFractionDigits: 2 }
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="fw-bold table-light">
                      <tr>
                        <td>Total</td>
                        <td className="text-success">
                          ₹
                          {filteredData
                            .reduce((a, b) => a + b.total_cash, 0)
                            .toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                        </td>
                        <td className="text-primary">
                          ₹
                          {filteredData
                            .reduce((a, b) => a + b.total_upi, 0)
                            .toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                        </td>
                        <td className="text-dark">
                          ₹
                          {filteredData
                            .reduce(
                              (a, b) => a + b.total_cash + b.total_upi,
                              0
                            )
                            .toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-center text-muted py-3">
                  No monthly revenue data found.
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default RevenueReport;
